import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { DynamicFieldConfig, PriceMapping } from '../types';
import {
  listPlaybookFields,
  createPlaybookField,
  updatePlaybookField,
  deletePlaybookField,
  listDropdownFields,
  updateDropdownFields,
  listPriceMappings,
  createPriceMapping,
  updatePriceMapping,
} from '../services/api';
import { getGameId } from '@core/utils/auth';

export const PLAYBOOK_QUERY_KEYS = {
  all: ['playbook', 'dynamic-fields'],
  lists: () => [...PLAYBOOK_QUERY_KEYS.all, 'list'],
  list: (moduleType: string) => [...PLAYBOOK_QUERY_KEYS.lists(), moduleType],
  dropdowns: ['playbook', 'dropdowns'],
  dropdownList: (moduleType: string) => [...PLAYBOOK_QUERY_KEYS.dropdowns, moduleType],
  priceMappings: ['playbook', 'price-mappings'],
};

export const usePlaybookFields = (moduleType: string) => {
  return useQuery({
    queryKey: PLAYBOOK_QUERY_KEYS.list(moduleType),
    queryFn: () => listPlaybookFields(moduleType),
    select: (resp) => resp.data,
    staleTime: 2 * 60 * 1000,
  });
};

export const useSavePlaybookField = (moduleType: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (field: DynamicFieldConfig) => {
      // If id exists, update. Otherwise, create.
      if (field.fieldId) {
        return updatePlaybookField(field);
      } else {
        return createPlaybookField(moduleType, field);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLAYBOOK_QUERY_KEYS.all });
    },
  });
};

export const useDeletePlaybookField = (moduleType: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deletePlaybookField(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLAYBOOK_QUERY_KEYS.all });
    },
  });
};

// ============================================
// DROPDOWN HOOKS
// ============================================

/**
 * Fetch dropdown fields for a module type (e.g., 'offer')
 */
export const useDropdownFields = (moduleType: string) => {
  return useQuery({
    queryKey: PLAYBOOK_QUERY_KEYS.dropdownList(moduleType),
    queryFn: () => listDropdownFields(moduleType),
    select: (resp) => resp.data,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Update dropdown fields (bulk update)
 */
export const useUpdateDropdownFields = (moduleType: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fields: DynamicFieldConfig[]) => updateDropdownFields(fields),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLAYBOOK_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: PLAYBOOK_QUERY_KEYS.dropdowns });
    },
  });
};

// ============================================
// PRICE MAPPING HOOKS
// ============================================

/**
 * Fetch all price mappings for the current game
 */
export const usePriceMappings = () => {
  return useQuery({
    queryKey: PLAYBOOK_QUERY_KEYS.priceMappings,
    queryFn: () => listPriceMappings(),
    select: (resp) => resp.data,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Create a new price mapping
 */
export const useCreatePriceMapping = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { price: number; platform: number; value: string }) => {
      const gameId = getGameId();
      return createPriceMapping({ gameId, ...payload });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLAYBOOK_QUERY_KEYS.priceMappings });
    },
  });
};

/**
 * Update an existing price mapping
 */
export const useUpdatePriceMapping = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: { price: number; platform: number; value: string } }) =>
      updatePriceMapping(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLAYBOOK_QUERY_KEYS.priceMappings });
    },
  });
};
